import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import Colour from 'components/utils/Colour'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { FlightsClassTypes } from 'constants/flight'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import { useRewardSeatsEnabled } from 'hooks/Flights/useRewardSeatsEnabled'
import MaldivesCharterBundleEducationBanner from './MaldivesCharterBundleEducationBanner'
import SolidPlaneDepartureIcon from 'LuxKit/Icons/solid/SolidPlaneDepartureIcon'
import Pill from 'LuxKit/Pill'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import flightBundleGlossaryMessages from 'i18n/flightBundleGlossaryMessages'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import styled from 'styled-components'
import CharterTileIndividualFlight from './CharterTileIndividualFlight'
import { rem } from 'polished'
import Divider from 'LuxKit/Divider'
import TextButton from 'LuxKit/Button/TextButton'
import Caption from 'LuxKit/Typography/Caption'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import LineBagIcon from 'LuxKit/Icons/line/LineBagIcon'
import LineLuggageIcon from 'LuxKit/Icons/line/LineLuggageIcon'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import ModalContext from 'contexts/ModalContext'
import CharterFlightDetailsModal from './CharterFlightDetailsModal'
import useCharterFlightJourneysForHotelDates from 'hooks/CharterFlights/useCharterFlightJourneysForHotelDates'
import { resolveShowConfiguredFeederCopy } from './utils'

// Hardcoded baggage values for Maldives charter - do not use API values
function useMaldivesCharterBaggage(intl: ReturnType<typeof useIntl>): Record<FlightsClassTypes, { carryOn: string, checked: string }> {
  return useMemo(() => {
    const standardCarryOn = intl.formatMessage({
      id: 'eXj247rHH3QgdsVTVKVO',
      defaultMessage: 'Carry-on 7kg',
      description: 'Standard carry-on baggage allowance for Maldives charter economy class',
    })
    const standardChecked = intl.formatMessage({
      id: 'b6x9mo4hk0WwF-guyJ4M',
      defaultMessage: 'Checked bag 23kg',
      description: 'Standard checked baggage allowance for Maldives charter economy class',
    })
    const businessCarryOn = intl.formatMessage({
      id: 'pk2KOSVk_xsV01XLub0F',
      defaultMessage: 'Carry-on 2 pieces up to 14kg, max 10kg per piece',
      description: 'Business class carry-on baggage allowance for Maldives charter',
    })
    const businessChecked = intl.formatMessage({
      id: 'PkzONCx5jmYKZuo-VYWF',
      defaultMessage: 'Checked bag 2x up to 46kg total, max 32kg per piece',
      description: 'Business class checked baggage allowance for Maldives charter',
    })

    return {
      [FlightsClassTypes.ECONOMY]: { carryOn: standardCarryOn, checked: standardChecked },
      [FlightsClassTypes.BUSINESS]: { carryOn: businessCarryOn, checked: businessChecked },
      [FlightsClassTypes.LUXROW]: { carryOn: standardCarryOn, checked: standardChecked },
      [FlightsClassTypes.LUXROW_PAX_ONE]: { carryOn: standardCarryOn, checked: standardChecked },
      [FlightsClassTypes.PREMIUM]: { carryOn: standardCarryOn, checked: standardChecked },
      [FlightsClassTypes.FIRST]: { carryOn: standardCarryOn, checked: standardChecked },
    }
  }, [intl])
}

const InlineBlock = styled.span`
  display: inline-block;
`

const FlightInfoGrid = styled.div`
  display: grid;
  gap: ${rem(8)};

  grid-template:
    'departingFlight'
    'returningFlight'
    'charterInfo'
    'fareInfo';

  ${mediaQueryUp.tablet} {
    gap: ${rem(8)} ${rem(20)};

    grid-template:
      'departingFlight divider returningFlight'
      'charterInfo charterInfo charterInfo'
      'fareInfo fareInfo fareInfo' / 4fr auto 3fr;
  }

  ${mediaQueryUp.desktop} {
    gap: ${rem(8)} ${rem(40)};

    grid-template:
      'departingFlight divider returningFlight fareInfo detailsButton'
      'charterInfo charterInfo charterInfo fareInfo detailsButton' / auto auto auto 1fr auto;
  }
`

const DepartingFlight = styled(CharterTileIndividualFlight)`
  grid-area: departingFlight;

  ${mediaQueryUp.desktop} {
    width: ${rem(290)};
  }
`

const ReturningFlight = styled(CharterTileIndividualFlight)`
  grid-area: returningFlight;

  ${mediaQueryUp.desktop} {
    width: ${rem(234)};
  }
`

const DesktopOnlyDivider = styled(Divider)`
  grid-area: divider;

  ${mediaQueryDown.mobile} {
    display: none;
  }
`

const FareInfoContainer = styled.div`
  grid-area: fareInfo;

  ${mediaQueryUp.desktop} {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`

const DetailsButtonContainer = styled.div`
  grid-area: detailsButton;
  display: flex;
  align-items: center;

  ${mediaQueryDown.tablet} {
    display: none;
  }
`

const CharterInfo = styled.div`
  grid-area: charterInfo;
`

const FlexBreakpoint = styled(CSSBreakpoint)`
  display: flex;
`

// Slight rotation matches the prototype's mobile plane glyph; not expressible via Group props.
const RotatedPlaneIcon = styled.div`
  flex-shrink: 0;
  margin-top: ${rem(2)};
  transform: rotate(12deg);
`

// Reserves the grid's eventual height so the card doesn't visibly grow when journeys arrive.
const TabletLoadingPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${rem(96)};

  ${mediaQueryUp.desktop} {
    min-height: ${rem(80)};
  }
`

const TileRoot = styled(Group)`
  width: 100%;
  background-color: transparent;
  padding-block: ${rem(16)};

  ${mediaQueryDown.mobile} {
    padding-inline: ${rem(16)};
  }
`

const RoundedCardPane = styled(Pane)`
  border-radius: ${rem(8)};
  overflow: hidden;
`

const OneWayFlightInfoGrid = styled.div`
  display: grid;
  gap: ${rem(16)};
  grid-template: 'departingFlight' 'fareInfo' 'charterInfo' 'detailsButton';

  ${mediaQueryUp.tablet} {
    align-items: center;
    column-gap: ${rem(40)};
    grid-template:
      'departingFlight fareInfo detailsButton'
      'charterInfo charterInfo charterInfo' / minmax(0, 350px) 1fr auto;
  }
`

const OneWayDetailsButtonContainer = styled.div`
  grid-area: detailsButton;
`

const OneWayFareInfoContainer = styled(FareInfoContainer)`
  justify-self: start;
`

interface Props {
  route: App.CharterBundleRoute
  checkInDate?: string
  checkOutDate?: string
  occupancies?: Array<App.Occupants>
  fareClass: FlightsClassTypes
  flightOrigin?: App.Airport
  /** Opens the charter trip calendar (e.g. after "View dates" on the reward-seats info modal). */
  onViewCharterDates?: () => void
}

const defaultRooms: Array<App.Occupants> = [{ adults: 2 }]

const strongTag = (chunks: React.ReactNode) => <strong>{chunks}</strong>

function ReturnCharterFlightTile({
  route,
  checkInDate,
  checkOutDate,
  occupancies = defaultRooms,
  fareClass,
  flightOrigin,
  onViewCharterDates,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const isRewardSeatsEnabled = useRewardSeatsEnabled()

  const routeInfo = CHARTER_FLIGHT_DETAILS[route]

  const {
    departingJourney,
    returningJourney,
    fetching: flightsFetching,
  } = useCharterFlightJourneysForHotelDates({
    charterBundleRoute: route,
    checkInDate,
    checkOutDate,
    occupancies,
    fareClass,
    feederOriginAirportCode: flightOrigin?.code,
  })

  const showExactDates = !!(checkInDate && checkOutDate)
  const useUndatedSegmentLabels = !showExactDates

  // Use hardcoded baggage values for Maldives charter instead of API values
  const maldivesCharterBaggage = useMaldivesCharterBaggage(intl)
  const charterBaggage = maldivesCharterBaggage[fareClass]

  const openDetailsModal = useCallback(() => {
    if (departingJourney || returningJourney) {
      showModal(<CharterFlightDetailsModal departingJourney={departingJourney} returningJourney={returningJourney} />)
    }
  }, [departingJourney, returningJourney, showModal])

  const needsFeederFlight = !!flightOrigin && flightOrigin.code !== routeInfo.originAirport.airportCode
  const hasFlights = !!(departingJourney && returningJourney)

  const charterOrigin = routeInfo.originAirport.cityDesc
  const charterDestination = routeInfo.destinationAirport.cityDesc

  const exclusiveLabel = intl.formatMessage(
    {
      id: 'k1dQC6LRCcvy5x-aCJ5F',
      defaultMessage: 'Exclusive to {brandName}',
      description: 'Pill label showing charter flight is exclusive to the brand',
    },
    { brandName: config.brandConfig.name },
  )

  const flightDetailsLabel = intl.formatMessage(flightBundleGlossaryMessages.flightDetails)

  const charterInfoText = intl.formatMessage(
    {
      id: '0XG52OyVOZHDYFe32Zi7',
      defaultMessage: 'Luxury Escapes Private Charter · Operated by {operatorName}',
      description: 'Charter flight operator information displaying the operator name for a private charter',
    },
    { operatorName: routeInfo.operatorName },
  )

  const mobileBundleHeading = intl.formatMessage({
    id: 'ueE6RcNkoUBKm7Y8UNtD',
    defaultMessage: 'Direct flights included in bundle:',
    description: 'First line of the mobile Maldives charter flight card title stack; teal lead-in before the origin and destination line.',
  })

  const mobileRouteHeading = intl.formatMessage(
    {
      id: '2355NBfU3qLeZAZnf8at',
      defaultMessage: '{origin} to {destination}',
      description: 'Second line of the mobile Maldives charter flight card title stack showing charter cities without the trailing return word.',
    },
    { origin: charterOrigin, destination: charterDestination },
  )

  const feederAvailabilityCaption = intl.formatMessage({
    id: 'M3VAj6mzlCUMZUzMd7Vx',
    defaultMessage: '(available when you book)',
    description: 'Muted caption under the connecting-flight upsell on the mobile Maldives charter tile; clarifies the add-on is chosen during booking.',
  })

  const isConfiguredFeederAirport = !!flightOrigin && routeInfo.feederFlightOrigins.some(airport => airport.airportCode === flightOrigin.code)

  const showConfiguredFeederCopy = resolveShowConfiguredFeederCopy({
    isConfiguredFeederAirport,
    hasFlights,
    departingFeederEstimatedPrice: departingJourney?.feederEstimatedPrice,
    returningFeederEstimatedPrice: returningJourney?.feederEstimatedPrice,
  })

  const configuredFeederMessage = needsFeederFlight && flightOrigin && showConfiguredFeederCopy && (
    <FormattedMessage
      id="N_QuPLJGLUjQgGb-h8rf"
      defaultMessage="<strong>{origin} connecting flights included</strong> - choose your times and airline at checkout."
      description="Charter tile message for configured feeder airports where connecting flights are included in the bundle; origin is the departure airport city name."
      values={{
        strong: strongTag,
        origin: flightOrigin.name,
      }}
    />
  )

  const nonConfiguredFeederMessage = needsFeederFlight && flightOrigin && !showConfiguredFeederCopy && (
    <FormattedMessage
      id="Z3gOl6Qammdq8YtzX9T5"
      defaultMessage="Flying from {origin}? Add your connecting flights to {destination} at checkout. Cost added to package price"
      description="Charter tile message for non-configured feeder airports prompting the user to add connecting flights; origin is the user's departure city, destination is the charter departure city."
      values={{
        origin: flightOrigin.name,
        destination: routeInfo.originAirport.cityDesc,
      }}
    />
  )

  if (!flightsFetching && showExactDates && (!!departingJourney !== !!returningJourney)) {
    return <TileRoot direction="vertical" gap={12}>
      <Group direction="vertical" desktopDirection="horizontal" horizontalAlign="start" desktopHorizontalAlign="space-between" verticalAlign="center" gap={8}>
        <HeadingTextBlock variant="heading5" startIcon={<SolidPlaneDepartureIcon colour="highlight-primary-dark" size="M" />}>
          <FormattedMessage
            id="NlsxyvhBSBRtmJDL2ctQ"
            defaultMessage="<highlight>Direct flight included in bundle:</highlight> {origin} to {destination}"
            description="Heading for a one-way Maldives charter summary showing the included flight direction on landing and hotel offer pages."
            values={{
              highlight: (chunks) => <InlineBlock><Colour value="highlight-primary-dark">{chunks}</Colour></InlineBlock>,
              origin: departingJourney ? charterOrigin : charterDestination,
              destination: departingJourney ? charterDestination : charterOrigin,
            }}
          />
        </HeadingTextBlock>
        <Pill palette="success">{exclusiveLabel}</Pill>
      </Group>
      <RoundedCardPane background="none" border="highlight-primary-dark" padding={0} data-testid="one-way-charter-summary">
        <Pane padding={16} desktopPadding={20} background="neutral-eight">
          <OneWayFlightInfoGrid>
            <DepartingFlight
              journey={(departingJourney ?? returningJourney)!}
              showExactDate
              segment={departingJourney ? 'departing' : 'returning'}
              route={route}
              desktopShowLogo
            />
            <OneWayFareInfoContainer>
              <Group direction="vertical" gap={4}>
                <CaptionTextBlock variant="medium" startIcon={<LineBagIcon colour="highlight-primary-dark" />}>
                  {charterBaggage.carryOn}
                </CaptionTextBlock>
                <CaptionTextBlock variant="medium" startIcon={<LineLuggageIcon colour="highlight-primary-dark" />}>
                  {charterBaggage.checked}
                </CaptionTextBlock>
              </Group>
            </OneWayFareInfoContainer>
            <OneWayDetailsButtonContainer>
              <TextButton kind="tertiary" onClick={openDetailsModal}>{flightDetailsLabel}</TextButton>
            </OneWayDetailsButtonContainer>
            <CharterInfo><Caption variant="medium">{charterInfoText}</Caption></CharterInfo>
          </OneWayFlightInfoGrid>
        </Pane>
        {isRewardSeatsEnabled && <MaldivesCharterBundleEducationBanner onViewCharterDates={onViewCharterDates} />}
      </RoundedCardPane>
      <BodyText variant="medium">{intl.formatMessage({
        id: '0XY839d36wWQanQZlcCe',
        defaultMessage: 'Your hotel and one-way charter flight are included. Choose your other flights at checkout for an additional cost. Flights are booked on separate tickets.',
        description: 'Explains partial charter bundle pricing and mandatory separate commercial flight selection on the hotel offer page.',
      })}
      </BodyText>
    </TileRoot>
  }

  if (!flightsFetching && !hasFlights) {
    return null
  }

  return (
    <TileRoot direction="vertical" gap={0} tabletGap={12}>
      <CSSBreakpoint min="tablet">
        <Group direction="vertical" desktopDirection="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
          <HeadingTextBlock variant="heading5" startIcon={<FlexBreakpoint min="tablet"><SolidPlaneDepartureIcon colour="highlight-primary-dark" size="M" /></FlexBreakpoint>}>
            <FormattedMessage
              id="AHTb1bcJkVb69JZX8Bgd"
              defaultMessage="<highlight>Direct flights included in bundle:</highlight> {origin} to {destination} return"
              description="Charter flight heading with return route showing origin and destination cities"
              values={{
                highlight: (chunks) => <InlineBlock><Colour value="highlight-primary-dark">{chunks}</Colour></InlineBlock>,
                origin: charterOrigin,
                destination: charterDestination,
              }}
            />
          </HeadingTextBlock>
          <Pill palette="success">{exclusiveLabel}</Pill>
        </Group>
      </CSSBreakpoint>

      <RoundedCardPane background="none" border="highlight-primary-dark" padding={0}>
        <CSSBreakpoint max="mobile">
          <Pane padding={16} desktopPadding={20} background="neutral-eight">
            {flightsFetching && <LoadingIndicator inline />}
            {!flightsFetching && hasFlights && (
              <Group direction="vertical" gap={16}>
                <Group direction="horizontal" verticalAlign="start" horizontalAlign="space-between" gap={12}>
                  <Group direction="vertical" gap={4}>
                    <BodyText variant="medium" weight="bold" colour="highlight-primary-dark">
                      {mobileBundleHeading}
                    </BodyText>
                    <BodyText variant="medium" weight="bold" colour="neutral-one">
                      {mobileRouteHeading}
                    </BodyText>
                    {needsFeederFlight && flightOrigin && <>
                      <BodyText variant="medium" colour="neutral-one">
                        {configuredFeederMessage}
                        {nonConfiguredFeederMessage}
                      </BodyText>
                      {!showConfiguredFeederCopy && (
                        <Caption variant="medium" colour="neutral-two">
                          {feederAvailabilityCaption}
                        </Caption>
                      )}
                    </>}
                  </Group>
                  <RotatedPlaneIcon aria-hidden>
                    <SolidPlaneDepartureIcon colour="highlight-primary-dark" size="M" />
                  </RotatedPlaneIcon>
                </Group>
                <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between" gap={8}>
                  <Pill palette="success">{exclusiveLabel}</Pill>
                  {showExactDates && <TextButton kind="secondary" size="small" onClick={openDetailsModal}>
                    {flightDetailsLabel}
                  </TextButton>}
                </Group>
              </Group>
            )}
          </Pane>
        </CSSBreakpoint>

        <CSSBreakpoint min="tablet">
          <Pane padding={16} desktopPadding={20} background="neutral-eight">
            {flightsFetching && !hasFlights && (
              <TabletLoadingPlaceholder>
                <LoadingIndicator inline />
              </TabletLoadingPlaceholder>
            )}
            {hasFlights && <FlightInfoGrid>
              <DepartingFlight
                journey={departingJourney}
                showExactDate={showExactDates}
                useUndatedSegmentLabels={useUndatedSegmentLabels}
                segment="departing"
                route={route}
                desktopShowLogo
              />
              <DesktopOnlyDivider kind="primary" direction="vertical" />
              <ReturningFlight
                journey={returningJourney}
                showExactDate={showExactDates}
                useUndatedSegmentLabels={useUndatedSegmentLabels}
                segment="returning"
                route={route}
                desktopShowLogo={false}
              />
              <FareInfoContainer>
                <Group direction="horizontal" desktopDirection="vertical" gap={4}>
                  <CaptionTextBlock variant="medium" startIcon={<LineBagIcon colour="highlight-primary-dark" />}>
                    {charterBaggage.carryOn}
                  </CaptionTextBlock>
                  <CaptionTextBlock variant="medium" startIcon={<LineLuggageIcon colour="highlight-primary-dark" />}>
                    {charterBaggage.checked}
                  </CaptionTextBlock>

                  {showExactDates && <>
                    <CSSBreakpoint max="tablet">
                      {intl.formatMessage(middleDotSeparatorMessage)}
                    </CSSBreakpoint>

                    <CSSBreakpoint max="tablet">
                      <CaptionTextBlock variant="medium">
                        <TextLink weight="regular" onClick={openDetailsModal}>{flightDetailsLabel}</TextLink>
                      </CaptionTextBlock>
                    </CSSBreakpoint>
                  </>}
                </Group>
              </FareInfoContainer>

              {showExactDates && <DetailsButtonContainer>
                <TextButton kind="tertiary" onClick={openDetailsModal}>{flightDetailsLabel}</TextButton>
              </DetailsButtonContainer>}

              <CharterInfo>
                <Caption variant="medium">
                  {charterInfoText}
                </Caption>
              </CharterInfo>
            </FlightInfoGrid>}
          </Pane>
        </CSSBreakpoint>

        {isRewardSeatsEnabled && (
          <MaldivesCharterBundleEducationBanner onViewCharterDates={onViewCharterDates} />
        )}
      </RoundedCardPane>

      {(configuredFeederMessage || nonConfiguredFeederMessage) && (
        <CSSBreakpoint min="tablet">
          <BodyText variant="medium">
            {configuredFeederMessage}
            {nonConfiguredFeederMessage}
          </BodyText>
        </CSSBreakpoint>
      )}
    </TileRoot>
  )
}

export default ReturnCharterFlightTile
